import React from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Users, ArrowRight, ShieldCheck, Search, Briefcase, Lock,
  Crown, Layers, Factory, CheckCircle2, Clock, PoundSterling
} from 'lucide-react';

const WHO_SHOULD_USE = [
  { icon: Briefcase, title: 'Multiple Vacancies', text: 'Hiring for several roles at once and need dedicated support.' },
  { icon: Layers, title: 'Specialized Skills', text: 'Hard-to-fill roles requiring niche or technical expertise.' },
  { icon: Lock, title: 'Confidential Hiring', text: 'Sensitive replacements or restructures that need discretion.' },
  { icon: Crown, title: 'Executive Recruitment', text: 'Senior leadership or C-suite hires.' },
  { icon: Factory, title: 'Bulk / Industry Staffing', text: 'Large-scale or industry-specific recruitment drives.' },
  { icon: ShieldCheck, title: 'Custom Requirements', text: 'Any hiring need that doesn’t fit a standard package.' },
];

const PROCESS = [
  { step: 1, title: 'Submit Your Request', text: 'Tell us about your company and the role(s) you need to fill.' },
  { step: 2, title: 'Team Review', text: 'Our recruitment team reviews your requirements within 1 business day.' },
  { step: 3, title: 'Candidate Search', text: 'We source, screen and shortlist candidates matched to your brief.' },
  { step: 4, title: 'Interviews & Offer', text: 'You interview shortlisted candidates and make your hiring decision.' },
  { step: 5, title: 'Placement', text: 'Once your new hire starts, the engagement is marked complete.' },
];

export default function DirectEmploymentSolutionsPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const handleCTA = () => {
    if (!isAuthenticated) { navigate('/login?redirect=/employer/direct-recruitment'); return; }
    if (user?.user_type !== 'employer') { navigate('/employer/direct-recruitment'); return; }
    navigate('/employer/direct-recruitment');
  };

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      {/* Hero */}
      <div className="bg-gradient-to-r from-[#003366] to-[#004d99] text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-6">
            <Users className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Direct Employment Solutions</h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
            Not every hire fits a standard package. Submit a personalized recruitment request and our
            dedicated team will handle the search, screening and follow-up for you.
          </p>
          <Button onClick={handleCTA} size="lg" className="bg-[#E11D48] hover:bg-[#E11D48]/90 text-white h-12 px-8">
            Submit Employment Request
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>

      {/* What it is */}
      <div className="max-w-5xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-2xl font-bold text-foreground mb-3">What is Direct Employment Solutions?</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Instead of purchasing a predefined recruitment bundle, employers with customized hiring needs can
            submit a detailed request describing exactly what they're looking for. Our recruitment team reviews
            it personally and manages the search, screening and shortlisting on your behalf.
          </p>
        </div>

        <h3 className="text-lg font-semibold text-center mb-6">Who Should Use This?</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
          {WHO_SHOULD_USE.map((item, i) => (
            <Card key={i}>
              <CardContent className="p-5">
                <div className="w-10 h-10 rounded-lg bg-[#003366]/10 flex items-center justify-center mb-3">
                  <item.icon className="w-5 h-5 text-[#003366]" />
                </div>
                <p className="font-semibold text-sm mb-1">{item.title}</p>
                <p className="text-sm text-muted-foreground">{item.text}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Process */}
        <h3 className="text-lg font-semibold text-center mb-6">Our Recruitment Process</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-16">
          {PROCESS.map((p) => (
            <div key={p.step} className="text-center">
              <div className="w-10 h-10 rounded-full bg-[#003366] text-white font-bold flex items-center justify-center mx-auto mb-3">
                {p.step}
              </div>
              <p className="font-semibold text-sm mb-1">{p.title}</p>
              <p className="text-xs text-muted-foreground">{p.text}</p>
            </div>
          ))}
        </div>

        {/* Benefits / Response time */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-16">
          <Card>
            <CardContent className="p-6 text-center">
              <Clock className="w-8 h-8 text-[#003366] mx-auto mb-3" />
              <p className="font-semibold mb-1">Fast Response</p>
              <p className="text-sm text-muted-foreground">Our team reviews and responds to every request within 1 business day.</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <CheckCircle2 className="w-8 h-8 text-[#003366] mx-auto mb-3" />
              <p className="font-semibold mb-1">Dedicated Support</p>
              <p className="text-sm text-muted-foreground">A named recruiter manages your request from search to placement.</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <Search className="w-8 h-8 text-[#003366] mx-auto mb-3" />
              <p className="font-semibold mb-1">Access to 26M+ Candidates</p>
              <p className="text-sm text-muted-foreground">We search our full candidate database to match your brief.</p>
            </CardContent>
          </Card>
        </div>

        {/* Pricing */}
        <Card className="bg-gradient-to-r from-[#003366] to-[#004d99] text-white border-0 mb-10">
          <CardContent className="p-8 text-center">
            <PoundSterling className="w-8 h-8 mx-auto mb-3 text-white/80" />
            <h3 className="text-xl font-bold mb-2">Simple, Success-Based Pricing</h3>
            <p className="text-white/80 max-w-xl mx-auto mb-6">
              We charge <strong>15% of the first annual income/salary</strong> of every employee successfully
              supplied through Direct Employment Solutions — you only pay when we deliver.
            </p>
            <Button onClick={handleCTA} className="bg-white text-[#003366] hover:bg-white/90">
              Submit Employment Request
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground">
          Already submitted a request?{' '}
          <Link to="/employer/dashboard" className="text-[#003366] font-medium hover:underline">
            Track it in your dashboard
          </Link>
        </p>
      </div>
    </div>
  );
}
